import EmptyState from 'components/EmptyState/EmptyState';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import 'features/Weather/assets/weatherDataDisplay.css';
import useLocation from 'hooks/useLocation';
import { Alert, Button, Container } from 'react-bootstrap';
import { useGetSkiReportsQuery } from 'services/weather/ski/ski.api';
import SkiResortAccordion from './components/SkiResortAccordion/SkiResortAccordion';
import './assets/skiReports.scss';

/**
 * Loads reports for the selected location and displays one accordion per resort.
 * Keeps the last available data visible while a new request is running.
 */
const SkiReports = () => {
  const { locations } = useLocation();
  const location = locations.effectiveLocation;
  const isLocationLoading = !location || location.isLoading;
  const { data, isLoading, isFetching, isError, refetch } = useGetSkiReportsQuery(location, {
    skip: isLocationLoading,
  });
  const reports = Array.isArray(data) ? data.filter(Boolean) : [];
  const hasReports = reports.length > 0;
  // A successful response with no resorts: the empty state carries the page
  // title itself, the way the other Weather features use it, so the heading
  // block steps aside instead of painting "Ski Reports" a second time.
  const isEmptyResult = !isError && !hasReports;

  // Only replace the page with a loader when there are no reports to keep on screen.
  if (isLocationLoading || ((isLoading || isFetching) && !hasReports)) {
    return <WidgetLoadingAnimation />;
  }

  return (
    <Container
      fluid
      className="ski-reports weather-data-display d-flex flex-column h-100 gap-3 px-0"
    >
      <WidgetTimeUpdate route="getSkiReports" />
      {!isEmptyResult && (
        <div className="weather-data-display-heading d-flex align-items-center justify-content-between gap-3 border-bottom pb-2">
          <h2 className="weather-data-display-title m-0 fw-bold lh-sm">Ski Reports</h2>
          {isFetching && hasReports && (
            <span className="small" role="status">
              Updating...
            </span>
          )}
        </div>
      )}

      {isError && (
        <Alert variant="danger" className="m-0 d-flex align-items-center flex-wrap gap-3">
          <p className="m-0 flex-grow-1">
            {hasReports
              ? 'Unable to update ski reports. Showing the last available results.'
              : 'Unable to load ski reports. Please try again.'}
          </p>
          <Button variant="primary" size="sm" onClick={refetch} disabled={isFetching}>
            Retry
          </Button>
        </Alert>
      )}

      {isEmptyResult && (
        <EmptyState
          title="Ski Reports"
          headingLevel="h2"
          message="No ski reports are available for this location."
        />
      )}

      {hasReports && (
        <div className="ski-reports-scroll flex-grow-1 overflow-auto">
          <div className="d-flex flex-column gap-3 pb-3">
            {reports.map((report, index) => (
              // Resort names are not guaranteed to be unique, so include the report's position.
              <SkiResortAccordion
                key={`${report.ski_area_name ?? 'resort'}-${index}`}
                report={report}
              />
            ))}
          </div>
        </div>
      )}
    </Container>
  );
};

export default SkiReports;
